import { useMemo, useState } from 'react'

import { useSuspenseInfiniteQuery } from '@tanstack/react-query'
import { differenceInDays } from 'date-fns'

import { useRequiredActionsByEntity, workbenchClient } from './workbench.service'
import { CustomerRequiredActionPageParams } from './workbench.service'
import { RequiredActionV2, RequiredActionV2Schema } from '../domain/requiredAction'
import { TASK_STATE } from '../domain/tasks'
import { RequiredActionTask } from '../domain/workbench'

export const requiredActionsTaskMapper = (task: RequiredActionTask): RequiredActionV2 => {
  return RequiredActionV2Schema.parse({
    id: task.taskDefinitionAttributes.id ?? '',
    entityId: task.entityID,
    entityType: task.entityType,
    active: task.state === TASK_STATE.AVAILABLE,
    category: task.taskDefinitionAttributes.category,
    completed: task.state === TASK_STATE.COMPLETED,
    completedBy: task.lastTouchedByUser,
    description: task.taskDefinitionAttributes.description,
    i18n: {
      term: task.taskDefinitionAttributes.i18nTerm,
      params: task.taskDefinitionAttributes.i18nTerm,
    },
    type: task.taskDefinitionAttributes.type,
    customerDueDate: task.timestamps.dueDate?.toISOString(),
    critical: task.timestamps.dueDate && differenceInDays(task.timestamps.dueDate, new Date()) < 1,
  })
}

export const useMappedCustomerRequiredActions = (baseParams: CustomerRequiredActionPageParams) => {
  const pageSize = baseParams.pageSize ?? 20
  const [visiblePageCount, setVisiblePageCount] = useState(1)

  const { data, fetchNextPage, isFetchingNextPage, hasNextPage, isLoading, isError, isFetching } =
    useSuspenseInfiniteQuery({
      queryKey: ['required-actions', { ...baseParams, pageSize }],
      queryFn: ({ pageParam = 1 }) =>
        workbenchClient.getRequiredActionsForCustomer({
          ...baseParams,
          page: pageParam,
          pageSize,
        }),
      initialPageParam: 1,
      getNextPageParam: (lastPage) => {
        return lastPage.pagination.page < lastPage.pagination.totalPages ? lastPage.pagination.page + 1 : undefined
      },
    })

  const allTasks = data?.pages.flatMap((page) => page.tasks) ?? []

  const filteredAndMapped = useMemo(() => {
    const slicedTasks = allTasks.slice(0, visiblePageCount * pageSize)
    const transformed = slicedTasks.map(requiredActionsTaskMapper)

    return {
      results: transformed,
      total: data?.pages?.[0]?.pagination.totalCount ?? 0,
      stats: data?.pages?.[0].stats,
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allTasks, visiblePageCount, pageSize])

  const showNextPage = async () => {
    if (hasNextPage) {
      await fetchNextPage()
      setVisiblePageCount((prev) => prev + 1)
    }
  }
  return {
    data: filteredAndMapped,
    fetchNextPage: showNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isFetching,
    isError,
  }
}

export const useMappedByEntityRequiredActions = (
  customerID: string,
  entityType: 'booking' | 'shipment',
  entityID: string
) => {
  const { data, isLoading, isFetching, isError } = useRequiredActionsByEntity(customerID, entityType, entityID)
  interface EntityData {
    tasks: Array<Omit<RequiredActionTask, 'entityID' | 'entityType'>>
    entityID: string
    entityType: 'booking' | 'shipment'
  }

  const requiredActionsTasks: RequiredActionTask[] = (data as EntityData).tasks.map((task) => ({
    ...task,
    entityID: (data as EntityData).entityID,
    entityType: (data as EntityData).entityType,
  }))

  const mapped = useMemo(() => {
    const transformedTasks: RequiredActionV2[] = requiredActionsTasks.map(requiredActionsTaskMapper).sort((a, b) => {
      const aDate = a.customerDueDate?.getTime?.() ?? Infinity
      const bDate = b.customerDueDate?.getTime?.() ?? Infinity
      return aDate - bDate
    })

    return {
      results: transformedTasks,
      total: transformedTasks.length,
    }
  }, [requiredActionsTasks])

  return {
    data: mapped,
    isFetching,
    isLoading,
    isError,
  }
}
